// Shared small UI primitives used across Hearth surfaces.
import { useEffect, useRef, type ReactNode } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Dialog a11y: save/restore focus, trap Tab, Escape to close, and label the
// dialog from its first heading (aria-labelledby) with no API burden on callers.
function useDialogA11y(
  ref: React.RefObject<HTMLElement>,
  onClose: () => void,
): void {
  useEffect(() => {
    const node = ref.current;
    const prev = document.activeElement as HTMLElement | null;

    // Label from the first heading if present.
    if (node && !node.getAttribute("aria-labelledby")) {
      const heading = node.querySelector("h1, h2, h3, h4");
      if (heading) {
        if (!heading.id) heading.id = `dlg-title-${Math.random().toString(36).slice(2, 8)}`;
        node.setAttribute("aria-labelledby", heading.id);
      }
    }

    // Move focus inside (first focusable, else the dialog itself).
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) { e.preventDefault(); node.focus(); return; }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === firstEl || active === node)) {
        e.preventDefault(); lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault(); firstEl.focus();
      }
    };
    node?.addEventListener("keydown", onKey);
    return () => {
      node?.removeEventListener("keydown", onKey);
      // Restore focus to whatever opened the dialog.
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [ref, onClose]);
}

export function Modal({
  children,
  onClose,
  wide,
}: {
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogA11y(ref, onClose);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        ref={ref}
        className={`modal card${wide ? " modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function Drawer({
  title,
  children,
  onClose,
  actions,
  side = "right",
  width,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  side?: "right" | "left";
  width?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  useDialogA11y(ref, onClose);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside
        ref={ref}
        className={`drawer drawer--${side}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={width ? { width } : undefined}
      >
        <div className="drawer__head">
          <h3>{title}</h3>
          <div className="flex">
            {actions}
            <button className="icon-btn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>
        <div className="drawer__body">{children}</div>
      </aside>
    </>
  );
}

export function Spinner() {
  return <span className="spinner" aria-label="Loading" />;
}

export function CenterLoad({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="center-load">
      <Spinner /> {label}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  sub,
  children,
}: {
  icon: string;
  title: string;
  sub?: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="es-icon">{icon}</div>
      <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, marginBottom: 6 }}>{title}</h3>
      {sub && <p className="muted" style={{ maxWidth: 420, margin: "0 auto" }}>{sub}</p>}
      {children && <div className="mt16">{children}</div>}
    </div>
  );
}

// App-level terminal state: dead link, deleted table, or a crash. Gives the
// player a clear message and a way back to the lobby instead of a dead screen.
export function TerminalState({
  icon = "🜂",
  title,
  body,
  action,
}: {
  icon?: string;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="terminal-state">
      <div className="terminal-state__icon">{icon}</div>
      <h2 className="terminal-state__title">{title}</h2>
      {body && <p className="terminal-state__body">{body}</p>}
      {action}
    </div>
  );
}

export function HpBar({
  hp,
  max,
  temp = 0,
  thin = false,
}: {
  hp: number;
  max: number;
  temp?: number;
  thin?: boolean;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (hp / max) * 100)) : 0;
  const ratio = max > 0 ? hp / max : 0;
  const cls = ratio <= 0.25 ? "crit" : ratio <= 0.5 ? "low" : "";
  const tempPct = max > 0 ? Math.min(100, (temp / max) * 100) : 0;
  return (
    <div className={thin ? "init-hpbar" : "hpbar"}>
      <div className={`${thin ? "init-hpbar__fill" : "hpbar__fill"} ${cls}`} style={{ width: `${pct}%`,
        background: thin ? (cls === "crit" ? "var(--hp-crit)" : cls === "low" ? "var(--hp-low)" : "var(--hp)") : undefined }} />
      {temp > 0 && !thin && <div className="hpbar__temp" style={{ left: `${pct}%`, width: `${tempPct}%` }} />}
    </div>
  );
}
